import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AlertTriangle, Zap } from 'lucide-react';

import { useViewModeTransition } from '../../context/ViewModeTransitionContext.tsx';
import { useAppSelector } from '../../hooks';
import { useViewModeAvailability } from './hooks/useViewModeAvailability.ts';

/**
 * The Render view shows textures baked with the optimal light distribution, so it can't show
 * an applied market match. When both are on, ask the user to switch to a view that does
 * (real-time render or measurement); once dismissed, a small note stays at the top.
 */
export function BakedRenderMarketMatchNotice() {
  const { t } = useTranslation();
  const { transitionToMode } = useViewModeTransition();
  const renderActive = useAppSelector((s) => !!s.workspace.activeViewModes?.render);
  const applied = useAppSelector((s) => s.workspace.appliedLampSimilaritySelection);
  const { measurementAvailable, realTimeAvailable } = useViewModeAvailability();

  const selectionKey = applied ? `${applied.lampId}-${applied.measurementFileId ?? ''}` : null;
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const visible = renderActive && selectionKey !== null;
  const open = visible && dismissedKey !== selectionKey;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDismissedKey(selectionKey);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, selectionKey]);

  if (!visible) return null;

  const switchView = () => transitionToMode(realTimeAvailable ? 'realTimeRender' : 'measurement');

  if (!open) {
    return (
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 flex max-w-[min(560px,calc(100%-32px))] items-center gap-2 rounded-full border border-amber-400/30 bg-[#1E1E1E]/95 py-1.5 pl-3 pr-1.5 text-[12px] text-amber-100 shadow-2xl">
        <AlertTriangle size={14} className="shrink-0 text-amber-400" />
        <span className="min-w-0 truncate">{t('workspace.bakedRenderNotice.chip')}</span>
        {(realTimeAvailable || measurementAvailable) && (
          <button
            type="button"
            onClick={switchView}
            className="shrink-0 rounded-full bg-white/10 px-2.5 py-1 font-medium text-white hover:bg-white/15"
          >
            {t('workspace.bakedRenderNotice.switch')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/45 p-4">
      <div
        role="alertdialog"
        aria-labelledby="baked-render-notice-title"
        aria-describedby="baked-render-notice-body"
        className="w-[400px] max-w-full rounded-xl border border-white/10 bg-[#1E1E1E] p-4 shadow-2xl"
      >
        <div className="flex gap-3">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-400" />
          <div className="min-w-0">
            <h3 id="baked-render-notice-title" className="text-[14px] font-semibold text-white">
              {t('workspace.bakedRenderNotice.title')}
            </h3>
            <p id="baked-render-notice-body" className="mt-1.5 text-[13px] leading-relaxed text-gray-300">
              {t('workspace.bakedRenderNotice.body')}
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => setDismissedKey(selectionKey)}
            className="h-9 rounded-full px-3.5 text-[13px] text-gray-300 hover:bg-white/5 hover:text-white"
          >
            {t('workspace.bakedRenderNotice.keep')}
          </button>
          {measurementAvailable && (
            <button
              type="button"
              onClick={() => transitionToMode('measurement')}
              className={`h-9 rounded-full px-3.5 text-[13px] font-medium ${
                realTimeAvailable
                  ? 'border border-white/15 text-gray-100 hover:bg-white/5'
                  : 'bg-lime-400 text-black hover:bg-lime-300'
              }`}
            >
              {t('workspace.bakedRenderNotice.measurement')}
            </button>
          )}
          {realTimeAvailable && (
            <button
              type="button"
              autoFocus
              onClick={() => transitionToMode('realTimeRender')}
              className="flex h-9 items-center gap-1.5 rounded-full bg-lime-400 px-3.5 text-[13px] font-medium text-black hover:bg-lime-300"
            >
              <Zap size={15} />
              {t('workspace.bakedRenderNotice.realTime')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
